// ============================================================
// Le formulaire « Règles de paie » → le référentiel c_values 25. Module PUR.
// Testé sans TestBed : hr-payroll-form.spec.ts.
// ============================================================
//
// CE FICHIER DÉCIDE DE CE QUI PART EN PAIE. Les seuils d'heures supplémentaires, la convention
// collective, le régime des congés, la plage de nuit, les titres-restaurant : tout ce que le
// gérant règle sur l'écran Organisation passe par cette traduction avant d'être écrit. Une erreur
// ici ne se voit pas à l'écran — elle se voit sur un bulletin de paie, un mois plus tard.
//
// Elle vivait dans le composant, donc ne pouvait pas être testée sans monter un navigateur.
// Ici, c'est une fonction de ses arguments : les bornes d'ordre public (un tiers pour les heures
// complémentaires, 44 h de moyenne sur 12 semaines, 48 h de plafond hebdomadaire) sont vérifiables.
//
// LE BORNAGE EST VOLONTAIREMENT REDONDANT avec `workTimeRulesOf` / `mealVoucherRulesOf`, qui
// reborneront tout à la relecture : on évite simplement d'ÉCRIRE EN BASE une valeur que la loi
// interdit. Une base qu'on relit un jour sans passer par le front doit déjà être juste.

import { minutesOfHhmm } from '../hr-schedule-engine';
import { mealVoucherRulesOf } from '../hr-meal-voucher';
import { monthDayOf } from '../hr-leave-rights';

/// Paliers d'heures supplémentaires tels que l'écran les édite (en HEURES, pas en minutes).
export interface OvertimeTierDraft {
	from_hours: number;
	to_hours: number | null;
	rate: number;
	kind: string;								// 'paid' | 'rtt' | 'recovered' | 'equivalence'…
	label?: string | null;
}

/// Paliers du temps PARTIEL, en pourcentage du contrat.
export interface PartTimeTierDraft {
	to_percent: number;
	rate: number;
}

export interface PremiumDraft {
	rate: number;
	rest_rate?: number | null;
	[k: string]: unknown;
}

export interface PayrollFormDraft {
	form: Record<string, any>;					// payrollForm.value
	overtimeTiers: OvertimeTierDraft[];
	partTimeTiers: PartTimeTierDraft[];
	premiums: PremiumDraft[];
	/// Dérivation de la politique d'heures supplémentaires (l'écran la fournit : elle vit déjà
	/// dans organization.component.ts et sert aussi à l'affichage).
	overtimeDestinationRules(policy: string, decider: string, employerDefault: string): Record<string, unknown>;
}

/// Refus de saisie : le message est celui que l'écran affiche, mot pour mot.
export type PayrollFormResult = { data: Record<string, unknown> } | { error: string };

export function isPayrollFormError(r: PayrollFormResult): r is { error: string } {
	return 'error' in r;
}

/// Les règles TELLES QU'ELLES SONT À L'ÉCRAN, prêtes pour le référentiel.
export function payrollRulesFromForm(d: PayrollFormDraft): PayrollFormResult {
	const v = d.form;
	const nightStart = minutesOfHhmm(v['night_start'] ?? '');
	const nightEnd = minutesOfHhmm(v['night_end'] ?? '');
	if (nightStart === null || nightEnd === null) {
		return { error: 'Plage de nuit incomplète' };
	}

	// Un palier majoré à 0 % n'a pas de sens et serait de toute façon reborné à 10 % ; un palier
	// RTT, récupéré ou d'équivalence, lui, est légitimement à 0 % — c'est tout son intérêt.
	const tiers = d.overtimeTiers
		.filter(t => t.rate > 0 || t.kind !== 'paid')
		.sort((a, b) => a.from_hours - b.from_hours)
		.map(t => ({
			from_min: Math.round(t.from_hours * 60),
			to_min: t.to_hours === null ? null : Math.round(t.to_hours * 60),
			rate: +t.rate,
			kind: t.kind,
			label: (t.label ?? '').trim(),
			contingent: t.kind === 'paid',
		}));

	const leaveUnit = v['leave_unit'] ?? 'ouvrables';
	const ouvres = leaveUnit === 'ouvres';

	return {
		data: {
			time_source: v['time_source'] === 'clock' ? 'clock' : 'planning',
			// Le zéro de tête d'un IDCC est cosmétique (« 01517 » = 1517) : le retirer évite deux
			// conventions identiques qui ne se reconnaîtraient pas.
			idcc: (v['idcc'] ?? '').toString().trim().replace(/^0+/, ''),
			idcc_label: (v['idcc_label'] ?? '').toString().trim(),
			// Identité KALI de la convention : c'est elle qui fabrique les liens de la fiche salarié.
			idcc_kali: (v['idcc_kali'] ?? '').toString().trim(),
			idcc_url: (v['idcc_url'] ?? '').toString().trim(),
			idcc_pdf: (v['idcc_pdf'] ?? '').toString().trim(),
			week_legal_min: Math.round(+(v['week_legal_hours'] ?? 35) * 60),
			overtime_tiers: tiers,
			week_working_days: Math.min(7, Math.max(1, +(v['week_working_days'] ?? 5))),
			leave_valuation: v['leave_valuation'] === 'planned' ? 'planned' : 'day_base',
			// Heures complémentaires : le tiers du contrat est un plafond d'ORDRE PUBLIC (L3123-9).
			part_time_tiers: d.partTimeTiers
				.filter(t => t.rate > 0 && t.to_percent > 0)
				.sort((a, b) => a.to_percent - b.to_percent)
				.map(t => ({ to_ratio: Math.min(1 / 3, t.to_percent / 100), rate: Math.max(10, +t.rate) })),
			complementary_limit_ratio: Math.min(1 / 3, +(v['complementary_limit_ratio'] ?? 0.1)),
			contingent_year_h: Math.max(0, +(v['contingent_year_h'] ?? 220)),
			cor_rate: +(v['cor_rate'] ?? 50) >= 100 ? 100 : 50,
			// La POLITIQUE fait foi : les trois réglages en découlent, plutôt que de coexister avec
			// elle. Sans cette dérivation, « toutes récupérées » pourrait s'enregistrer avec un
			// « choix par défaut : payer » resté d'un réglage précédent — et le rapport paierait.
			...d.overtimeDestinationRules(
				v['overtime_policy'] ?? 'pay', v['rcr_decider'] ?? 'employer', v['rcr_employer_default'] ?? 'pay'),
			overtime_leave_basis: v['overtime_leave_basis'] === 'worked' ? 'worked' : 'worked_plus_paid_leave',
			// 48 h : plafond hebdomadaire absolu (L3121-20). Un plafond conventionnel ne peut être
			// que PLUS strict — jamais plus permissif.
			max_week_conventional_min: v['max_week_conventional_hours'] == null || +v['max_week_conventional_hours'] <= 0
				? null : Math.min(2880, Math.round(+v['max_week_conventional_hours'] * 60)),
			// 46 h : plafond de la moyenne sur 12 semaines (L3121-23), au-delà du 44 h de défaut.
			max_week_avg12_min: Math.min(2760, Math.round(+(v['max_week_avg12_hours'] ?? 44) * 60)),
			premiums: d.premiums.map(p => ({
				...p, rate: Math.max(0, +p.rate), rest_rate: Math.max(0, +(p.rest_rate ?? 0)),
			})),
			leave: {
				acquisition_unit: leaveUnit,
				// Les deux régimes sont ÉQUIVALENTS en droit : 2,5 j ouvrables = 2,08 j ouvrés,
				// 30 = 25. Ils ne se panachent pas — l'unité décide des deux chiffres.
				days_per_month: ouvres ? 2.08 : 2.5,
				max_days: ouvres ? 25 : 30,
				reference_start: monthDayOf(v['leave_reference_month'], v['leave_reference_day']),
				taking_period_start: v['leave_taking_start'] ?? '05-01',
				taking_period_end: v['leave_taking_end'] ?? '10-31',
				fractionation_enabled: !!v['leave_fractionation'],
				fractionation_waiver_required: !!v['leave_waiver_required'],
				fractionation_waiver_blocking: !!v['leave_waiver_blocking'],
				closing_enabled: !!v['leave_closing_enabled'],
				closing_employee_ack: !!v['leave_closing_ack'],
				closing_notice_days: Math.max(0, Math.min(180, Math.round(+(v['leave_closing_notice'] ?? 30)))),
				carryover_default: v['leave_carryover_default'] === 'lost' ? 'lost' : 'carry',
			},
			night_start_min: nightStart,
			night_end_min: nightEnd,
			night_rate: +(v['night_rate'] ?? 0),
			sunday_rate: +(v['sunday_rate'] ?? 0),
			holiday_rate: +(v['holiday_rate'] ?? 0),
			employer_charges_rate: +(v['employer_charges_rate'] ?? 0),
			// Titres-restaurant. `mealVoucherRulesOf` reborne ensuite tout ce qui doit l'être (part
			// patronale 0..1, charnière dans la journée) : ici on ne fait que traduire le formulaire.
			meal_vouchers: {
				enabled: !!v['meal_enabled'],
				min_morning_min: Math.round(+(v['meal_morning_min'] ?? 180)),
				min_afternoon_min: Math.round(+(v['meal_afternoon_min'] ?? 180)),
				pivot_min: minutesOfHhmm(v['meal_pivot'] ?? '') ?? 720,
				face_value: +(v['meal_face_value'] ?? 0),
				employer_share: Math.round(+(v['meal_employer_share'] ?? 60)) / 100,
				exoneration_cap: Math.max(0, +(v['meal_exoneration_cap'] ?? 0)),
			},
		},
	};
}

/// Aperçu des titres-restaurant PENDANT la saisie : « l'entreprise paie 5,40 €, le salarié 3,60 €
/// retenus sur son bulletin » répond à la question que « part patronale 60 % » laisse ouverte.
/// Calculé depuis le formulaire EN COURS, pas depuis les règles enregistrées.
export function mealDraftOf(form: Record<string, any>) {
	return mealVoucherRulesOf({
		enabled: !!form['meal_enabled'],
		min_morning_min: form['meal_morning_min'],
		min_afternoon_min: form['meal_afternoon_min'],
		pivot_min: minutesOfHhmm(form['meal_pivot'] ?? '') ?? 720,
		face_value: form['meal_face_value'],
		employer_share: (+(form['meal_employer_share'] ?? 0)) / 100,
		exoneration_cap: form['meal_exoneration_cap'],
	});
}
